import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import {
  Autocomplete,
  Avatar,
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import useDebounce from '../../../hooks/useDebounce';
import {
  AppointmentLanguage,
  AppointmentTimeTag,
  FindAllAppointmentsParams,
  SimplifiedAppointment,
} from '../../../interfaces/appointments';
import { Pagination } from '../../../interfaces/common';
import { InternalCoreUser } from '../../../interfaces/core.adapter';
import messagesService from '../../../services/appointments/messages';
import coreService from '../../../services/core';
import { DEFAULT_PAGE_SIZE, INITIAL_PAGINATION_STATE } from '../../../util/constants';
import { formatDateStringToMMDDYYYYAndHour } from '../../../util/dateFormat';
import { CustomTable } from '../../common/CustomTable';
import { ClinicFilter } from '../ClinicFilter';
import '../communication-center.css';

type Props = {
  breadcrumbs: React.ReactNode;
};

export const AppointmentsCenter = ({ breadcrumbs }: Props) => {
  const [appointments, setAppointments] = useState<SimplifiedAppointment[]>([]);
  const [page, setPage] = useState<number>(1);
  const [filters, setFilters] = useState<FindAllAppointmentsParams>({});
  const [pagination, setPagination] = useState<Pagination>(INITIAL_PAGINATION_STATE);
  const [members, setMembers] = useState<InternalCoreUser[]>([]);
  const [providers, setProviders] = useState<InternalCoreUser[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRowId, setMenuRowId] = useState<number | null>(null);

  const notify = useNotify();

  const isMenuOpen = (rowId: number) => menuRowId === rowId;

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, rowId: number) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuRowId(rowId);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setMenuRowId(null);
  };

  const fetchAppointments = async (filters?: FindAllAppointmentsParams) => {
    setLoading(true);
    const response = await messagesService.findAllAppointments({
      ...(filters &&
        Object.entries(filters).reduce(
          (prev, [filter, value]) => ({ ...prev, ...(value !== undefined && { [filter]: value }) }),
          {},
        )),
      page,
      pageSize: DEFAULT_PAGE_SIZE,
    });

    if (!response?.data || !response.pagination) {
      notify(response.message || 'There is a problem fetching appointments');
      return;
    }

    setPagination(response.pagination);
    setAppointments(response.data);
    setLoading(false);
  };

  const updateAppointment = async (coreAppointmentId: number, confirmationStatus: boolean) => {
    setLoading(true);
    const response = await messagesService.updateAppointmentConfirmationStatus({
      confirmationStatus,
      coreAppointmentId,
    });

    if (!response?.data) {
      notify(response.message || 'There is a problem updating this particular appointment');
      return;
    }

    await fetchAppointments(filters);
    setLoading(false);
  };

  const searchUsersByTerm = async ({
    searchTerm,
    target,
  }: {
    searchTerm?: string | null;
    target: 'providers' | 'members';
  }) => {
    if (!searchTerm) {
      return [];
    }

    return (await coreService.fetchMembers({ searchTerm, target })).data;
  };

  const handleFilterChanges = (filter?: {
    [filter: string]: boolean | number | string | undefined | string[] | number[];
  }) => {
    const updatedFilters = {
      ...filters,
      ...filter,
    };
    setFilters(updatedFilters);
    debouncedFetchAppointments(updatedFilters);
  };

  const debouncedFetchMembers = useDebounce(async (param: { searchTerm: string }) => {
    setMembers(await searchUsersByTerm({ ...param, target: 'members' }));
  });

  const debouncedFetchProviders = useDebounce(async (param: { searchTerm: string }) => {
    setProviders(await searchUsersByTerm({ ...param, target: 'providers' }));
  });

  const debouncedFetchAppointments = useDebounce(fetchAppointments);

  useEffect(() => {
    (async () => await fetchAppointments(filters))();
  }, [page]);

  return (
    <>
      <Box className='tab-section appointments-center-section' marginTop={0}>
        <Box className='appointments-center-header'>
          <Box
            display='flex'
            justifyContent='space-between'
            alignContent='center'
            marginBottom='5px'>
            {breadcrumbs}
          </Box>

          <Box display='flex' alignItems='flex-start' gap={1} mt={2} mb={5} flexWrap='wrap'>
            <Box display='flex' flexDirection='column' alignItems='flex-start'>
              <Typography
                sx={{
                  '&.MuiTypography-root': { fontSize: '13px' },
                }}>
                Member
              </Typography>
              <Autocomplete
                fullWidth
                className='member-selector'
                options={members}
                filterOptions={(x) => x}
                getOptionLabel={(option) => `${option.fullName} ID: ${option.id}`}
                onChange={(e, v) => handleFilterChanges({ coreMemberId: v?.id || '' })}
                noOptionsText='No members found'
                value={members.find((m) => m.id === Number(filters.coreMemberId)) || undefined}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant='outlined'
                    placeholder='Enter a member name'
                    onChange={(e) => debouncedFetchMembers({ searchTerm: e.target.value })}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <Avatar
                            sx={{
                              color: 'action.active',
                              mr: 1,
                            }}
                          />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component='li' {...props}>
                    <Avatar sx={{ mr: 2, bgcolor: '#007bff' }}>
                      {`${option.fullName?.[0]}${option.fullName?.[1]}`.toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography fontSize={'0.8rem'}>{option.fullName}</Typography>
                      <Typography variant='caption' color='textSecondary'>
                        ID: {option.id}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            </Box>

            <Box display='flex' flexDirection='column' alignItems='flex-start'>
              <Typography
                sx={{
                  '&.MuiTypography-root': { fontSize: '13px' },
                }}>
                Provider
              </Typography>
              <Autocomplete
                fullWidth
                className='member-selector'
                options={providers}
                filterOptions={(x) => x}
                getOptionLabel={(option) => `${option.fullName} ID: ${option.id}`}
                onChange={(e, v) => handleFilterChanges({ coreProviderId: v?.id || '' })}
                noOptionsText='No providers found'
                value={providers.find((m) => m.id === Number(filters.coreProviderId)) || undefined}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant='outlined'
                    placeholder='Enter an agent name'
                    onChange={(e) => debouncedFetchProviders({ searchTerm: e.target.value })}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <Avatar
                            sx={{
                              color: 'action.active',
                              mr: 1,
                            }}
                          />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component='li' {...props}>
                    <Avatar sx={{ mr: 2, bgcolor: '#007bff' }}>
                      {`${option.fullName?.[0]}${option.fullName?.[1]}`.toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography fontSize={'0.8rem'}>{option.fullName}</Typography>
                      <Typography variant='caption' color='textSecondary'>
                        ID: {option.id}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            </Box>

            <Box
              display='flex'
              flexDirection='column'
              alignItems='flex-start'
              sx={{
                width: '200px',
              }}>
              <Typography
                sx={{
                  '&.MuiTypography-root': { fontSize: '13px' },
                }}>
                Time Tag
              </Typography>
              <Autocomplete
                fullWidth
                key='autocomplete-time-tag'
                size='small'
                options={Object.values(AppointmentTimeTag)}
                getOptionLabel={(option) =>
                  `${option[0].toUpperCase()}${option.slice(1).toLowerCase()}`
                }
                onChange={(event, value) => {
                  if (!!value) {
                    handleFilterChanges({ timeTag: value });
                  } else {
                    handleFilterChanges({ timeTag: undefined });
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={{
                      width: '200px',
                    }}
                    fullWidth
                    placeholder='Select a time tag'
                  />
                )}
              />
            </Box>

            <Box
              display='flex'
              flexDirection='column'
              alignItems='flex-start'
              sx={{
                width: '200px',
              }}>
              <Typography
                sx={{
                  '&.MuiTypography-root': { fontSize: '13px' },
                }}>
                Confirmation Status
              </Typography>
              <Autocomplete
                fullWidth
                key='autocomplete-confirmation-status'
                size='small'
                options={['confirmed', 'unconfirmed']}
                getOptionLabel={(option) =>
                  `${option[0].toUpperCase()}${option.slice(1).toLowerCase()}`
                }
                onChange={(event, value) => {
                  if (value === 'confirmed') {
                    handleFilterChanges({ confirmationStatus: true });
                  } else if (value === 'unconfirmed') {
                    handleFilterChanges({ confirmationStatus: false });
                  } else {
                    handleFilterChanges({ confirmationStatus: undefined });
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={{
                      width: '200px',
                    }}
                    fullWidth
                    placeholder='Select a status'
                  />
                )}
              />
            </Box>

            <Box
              display='flex'
              flexDirection='column'
              alignItems='flex-start'
              sx={{
                width: '150px',
              }}>
              <Typography
                sx={{
                  '&.MuiTypography-root': { fontSize: '13px' },
                }}>
                Language
              </Typography>
              <Autocomplete
                fullWidth
                key='autocomplete-language'
                size='small'
                options={Object.values(AppointmentLanguage)}
                getOptionLabel={(option) => {
                  const labels: Record<AppointmentLanguage, string> = {
                    [AppointmentLanguage.EN]: 'English',
                    [AppointmentLanguage.ES]: 'Spanish',
                    [AppointmentLanguage.AR]: 'Arabic',
                  };
                  return labels[option];
                }}
                onChange={(event, value) => {
                  handleFilterChanges({ appointmentLanguage: value || undefined });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={{
                      width: '150px',
                    }}
                    fullWidth
                    placeholder='Select language'
                  />
                )}
              />
            </Box>

            <Box display='flex' flexDirection='column' alignItems='flex-start'>
              <Typography
                sx={{
                  '&.MuiTypography-root': { fontSize: '13px' },
                }}>
                Start Date From
              </Typography>
              <TextField
                type='date'
                size='small'
                value={filters.startAtFrom || ''}
                onChange={(e) => handleFilterChanges({ startAtFrom: e.target.value || undefined })}
                sx={{ width: '170px' }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <Box display='flex' flexDirection='column' alignItems='flex-start'>
              <Typography
                sx={{
                  '&.MuiTypography-root': { fontSize: '13px' },
                }}>
                Start Date To
              </Typography>
              <TextField
                type='date'
                size='small'
                value={filters.startAtTo || ''}
                onChange={(e) => handleFilterChanges({ startAtTo: e.target.value || undefined })}
                sx={{ width: '170px' }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <ClinicFilter
              selectedClinicIds={filters.clinicIds || []}
              includeUnassigned={!!filters.includeUnassigned}
              onChange={({ clinicIds, includeUnassigned }) =>
                handleFilterChanges({ clinicIds, includeUnassigned })
              }
            />
          </Box>
        </Box>

        <CustomTable
          columns={[
            {
              key: 'coreAppointmentId',
              label: 'Appt. ID',
              width: '60px',
            },
            { key: 'member', label: 'Member', width: '100px' },
            { key: 'provider', label: 'Provider', width: '100px' },
            { key: 'startDate', label: 'Start Date', width: '130px' },
            { key: 'appointmentCode', label: 'Appt. Code', width: '80px' },
            { key: 'status', label: 'Status', width: '80px' },
            { key: 'confirmed', label: 'Confirmed', width: '50px', align: 'center' },
            { key: 'timeTag', label: 'Time Tag', width: '60px' },
            { key: 'noShow', label: 'No Show', width: '50px' },
            { key: 'actions', label: 'Actions', width: '30px', align: 'right' },
          ]}
          defaultText='No registered appointments'
          key={'communications-center-appointments-table'}
          loading={loading}
          rows={appointments.map((data) => {
            const { appointmentTypeCode } = data;

            return {
              coreAppointmentId: <p style={{ margin: 0 }}>{data.coreAppointmentId}</p>,
              member: (
                <Box flexDirection='column' display='flex'>
                  <p style={{ margin: 0 }}>{data.coreMemberFullname}</p>
                  <p style={{ margin: 0, color: '#8f8f8f' }}>ID: {data.coreMemberId}</p>
                </Box>
              ),
              provider: (
                <Box flexDirection='column' display='flex'>
                  <p style={{ margin: 0 }}>{data.coreProviderFullname}</p>
                  <p style={{ margin: 0, color: '#8f8f8f' }}>ID: {data.coreProviderId}</p>
                </Box>
              ),
              startDate: <p>{formatDateStringToMMDDYYYYAndHour(data.appointmentStartAt)}</p>,
              confirmed: data.confirmed && <CheckCircleOutlineIcon sx={{ color: '#389FBA' }} />,
              noShow: (
                <Box display='flex' alignContent='center' justifyContent='center'>
                  {data.coreNoShow && (
                    <PersonOffIcon
                      sx={{
                        color: '#E42020',
                      }}
                    />
                  )}
                </Box>
              ),
              appointmentCode: <p>{appointmentTypeCode}</p>,
              status: <Chip label={data.coreStatus} />,
              timeTag: (
                <Box display='flex' alignContent='center' justifyContent='center'>
                  {data.timeTag && <Chip label={`${data.timeTag}h`} />}
                </Box>
              ),
              actions: (
                <Box>
                  <IconButton
                    onClick={(e) => handleOpenMenu(e, data.coreAppointmentId)}
                    size='small'>
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    open={isMenuOpen(data.coreAppointmentId)}
                    anchorEl={menuAnchorEl}
                    onClose={handleCloseMenu}>
                    <MenuItem
                      onClick={() => {
                        updateAppointment(data.coreAppointmentId, !data.confirmed);
                        handleCloseMenu();
                      }}>
                      Set as {data.confirmed ? 'unconfirmed' : 'confirmed'}
                    </MenuItem>
                  </Menu>
                </Box>
              ),
            };
          })}
          pagination={{
            rowsPerPageOptions: [DEFAULT_PAGE_SIZE],
            rowPerPage: pagination.perPage,
            page: pagination.currentPage,
            totalCount: pagination.totalCount,
            onPageChange: (page: number) => setPage(page),
          }}
        />
      </Box>
    </>
  );
};
